import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const InputPanel = ({
  roughIdea,
  setRoughIdea,
  codeSnippet,
  setCodeSnippet,
  showCodeInput,
  setShowCodeInput,
  inputMode,
  setInputMode,
  selectedStyle,
  setSelectedStyle,
  availableStyles,
  onGenerate,
  onContinueManual,
  isGenerating
}) => {
  const [showStylePicker, setShowStylePicker] = useState(false);
  const textareaRef = useRef(null);
  const stylePickerRef = useRef(null);

  // Auto-grow textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.max(120, textareaRef.current.scrollHeight) + 'px';
    }
  }, [roughIdea]);

  // Close style picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (stylePickerRef.current && !stylePickerRef.current.contains(e.target)) {
        setShowStylePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentStyle = availableStyles?.find(s => s.value === selectedStyle);

  const handleSubmit = () => {
    if (!roughIdea?.trim()) return;
    if (inputMode === 'ai') onGenerate();
    else onContinueManual();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="max-w-3xl mx-auto w-full">
      {/* Mode Toggle */}
      <div className="flex items-center justify-center gap-1 mb-6">
        <button
          onClick={() => setInputMode('ai')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
            inputMode === 'ai'
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <span className="flex items-center gap-2">
            <Icon name="Sparkles" size={14} />
            AI Generate
          </span>
        </button>
        <button
          onClick={() => setInputMode('manual')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
            inputMode === 'manual'
              ? 'bg-muted text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <span className="flex items-center gap-2">
            <Icon name="PenLine" size={14} />
            Write Manually
          </span>
        </button>
      </div>

      {/* Main Input Card */}
      <div className="bg-card rounded-xl border border-border shadow-lg">
        <div className="p-4">
          <textarea
            ref={textareaRef}
            value={roughIdea}
            onChange={(e) => setRoughIdea(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              inputMode === 'ai'
                ? "Share your technical idea, experience, or insight... AI will transform it into platform-optimized posts."
                : "Write your post content directly. It will be used as-is for your selected platforms."
            }
            className="w-full bg-transparent text-foreground placeholder:text-muted-foreground text-base leading-relaxed resize-none focus:outline-none min-h-[120px]"
            style={{ fontFamily: "'Source Sans 3', sans-serif" }}
          />

          {/* Code Snippet Area */}
          {showCodeInput && (
            <div className="mt-3 rounded-lg overflow-hidden border border-border">
              <div className="flex items-center justify-between px-3 py-2 bg-muted/80 border-b border-border">
                <span className="text-xs text-muted-foreground font-medium" style={{ fontFamily: "'Fira Code', monospace" }}>Code Snippet</span>
                <button onClick={() => { setShowCodeInput(false); setCodeSnippet(''); }} className="text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="XCircle" size={14} />
                </button>
              </div>
              <textarea
                value={codeSnippet}
                onChange={(e) => setCodeSnippet(e.target.value)}
                placeholder={`// Paste your code snippet here\nconst example = "Hello World";`}
                className="w-full h-32 px-3 py-3 bg-background text-emerald-400 placeholder:text-muted-foreground text-sm resize-none focus:outline-none"
                style={{ fontFamily: "'Fira Code', monospace" }}
              />
            </div>
          )}
        </div>

        {/* Bottom Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <div className="flex items-center gap-2">
            {/* Writing Style Picker - Only in AI mode — opens DOWNWARD */}
            {inputMode === 'ai' && (
              <div className="relative" ref={stylePickerRef}>
                <button
                  onClick={() => setShowStylePicker(!showStylePicker)}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted rounded-lg transition-all duration-200"
                >
                  <Icon name="Palette" size={14} />
                  <span>{currentStyle?.label || 'Writing Style'}</span>
                  <Icon name="ChevronDown" size={12} />
                </button>

                {/* Style Dropdown — opens downward */}
                {showStylePicker && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden">
                    <div className="p-2">
                      <p className="text-xs text-muted-foreground px-2 py-1.5 font-medium uppercase tracking-wider">Writing Style</p>
                      {availableStyles?.map((style) => (
                        <button
                          key={style.value}
                          onClick={() => { setSelectedStyle(style.value); setShowStylePicker(false); }}
                          className={`w-full text-left px-3 py-2.5 rounded-lg transition-all duration-150 ${
                            selectedStyle === style.value
                              ? 'bg-muted text-foreground'
                              : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-sm font-medium block">{style.label}</span>
                              <span className="text-xs text-muted-foreground block mt-0.5">{style.description}</span>
                            </div>
                            {selectedStyle === style.value && (
                              <Icon name="Check" size={14} className="text-foreground flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Code Toggle */}
            {!showCodeInput && (
              <button
                onClick={() => setShowCodeInput(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted hover:bg-muted rounded-lg transition-all duration-200"
              >
                <Icon name="Code2" size={14} />
                <span>Add Code</span>
              </button>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!roughIdea?.trim() || isGenerating}
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${
              roughIdea?.trim() && !isGenerating
                ? 'bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] text-white hover:opacity-90'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {isGenerating ? (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <Icon name="ArrowUp" size={16} />
            )}
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground text-center mt-3">
        {inputMode === 'ai'
          ? 'Press ⌘+Enter to generate • AI will optimize for LinkedIn & X'
          : 'Press ⌘+Enter to continue • Your content will be used directly'
        }
      </p>
    </div>
  );
};

export default InputPanel;
